import { Country } from '@skippay/core/@types/country';
import type { AttrValue } from '@skippay/core/utils/objectUtils';

/* eslint-disable camelcase */
import { jarabotDevStore } from './devStore';
import { showPendingIndicator } from './widgetPendingIndicator';

type QueuedCall = () => void;

export type JarabotAttrValue = AttrValue | string[];

export type JarabotAttributes = Record<string, JarabotAttrValue | undefined>;

export interface JarabotUserIdentity {
	[key: string]: string | undefined;
	name?: string;
	email?: string;
	// eslint-disable-next-line camelcase
	phone_number?: string;
	// eslint-disable-next-line camelcase
	country_code?: string;
}

export interface JarabotContactAttributes extends JarabotAttributes {
	'contract.type'?: string;
	'contract.activeProducts'?: string[];
	'contract.availableAmountState'?: string;
	'contract.customerBlockingReasons'?: string[];
	kyc_verified?: boolean;
	'customer.amlLegalReview.isRequested'?: boolean;
}

export const JARABOT_EVENTS = {
	CUSTOMER_STUCK_ON_SMS: 'customer_stuck_on_sms_in_gate',
	CUSTOMER_STUCK_ON_SMS_HANDOFF: 'customer_stuck_on_sms_in_gate_handoff',
	CUSTOMER_STUCK_ON_IDENTITY_VERIFICATION: 'customer_stuck_on_identity_verification',
	CUSTOMER_INTERRUPTED_IDENTITY_VERIFICATION: 'customer_interrupted_identity_verification',
	CUSTOMER_STUCK_ON_CREDIT_APPLICATION_START: 'customer_stuck_on_credit_application_start',
	CUSTOMER_STUCK_ON_REVOLVING_SUMMARY_BEFORE_PERSONAL_DATA:
		'customer_stuck_on_revolving_summary_before_personal_data',
	CUSTOMER_KYC_NAME_MISMATCH_CANCELLATION: 'customer_KYC_name_mismatch_cancellation_in_gate',
	CUSTOMER_INSURANCE_OFFER: 'customer_insurance_offer',
	CUSTOMER_STUCK_ON_INCOME_VERIFICATION: 'customer_stuck_on_income_verification',
	CUSTOMER_FAILED_INCOME_VERIFICATION: 'customer_failed_income_verification',
} as const;

export type JarabotEvent = (typeof JARABOT_EVENTS)[keyof typeof JARABOT_EVENTS];

export const JARABOT_ACTIONS = {
	CONTINUE_APPLICATION: 'continue_application',
} as const;

export const JARABOT_STORAGE_KEYS = {
	SMS_TRIGGER_1_DELAY: '__jarabot_trigger1_delay',
	SMS_TRIGGER_2_DELAY: '__jarabot_trigger2_delay',
	IDENTITY_DELAY: '__jarabot_identity_delay',
	IDENTITY_SIMULATE_BANKID_STARTED: '__jarabot_identity_simulate_bankid_started',
	CREDIT_APP_START_DELAY: '__jarabot_credit_app_start_delay',
	REVOLVING_SUMMARY_DELAY: '__jarabot_revolving_summary_delay',
	INSURANCE_OFFER_DELAY: '__jarabot_insurance_offer_delay',
	INCOME_VERIFICATION_STUCK_DELAY: '__jarabot_income_verification_stuck_delay',
	INCOME_VERIFICATION_FAILED_DELAY: '__jarabot_income_verification_failed_delay',
} as const;

export const JARABOT_DELAYS = {
	SMS_TRIGGER_1: 30_000,
	SMS_TRIGGER_2: 60_000,
	IDENTITY: 30_000,
	CREDIT_APP_START: 20_000,
	REVOLVING_SUMMARY: 10_000,
	INSURANCE_OFFER: 10_000,
	INCOME_VERIFICATION_STUCK: 10_000,
	INCOME_VERIFICATION_FAILED: 600,
} as const;

const pendingQueue: QueuedCall[] = [];
let isReady = false;

const isDev = process.env.TARGET_ENV !== 'prod';

function log(method: string, payload?: unknown): void {
	if (isDev) {
		// eslint-disable-next-line no-console
		console.log('[Jarabot]', method, payload ?? '');
	}
}

function track(mirror: () => void): void {
	if (!isDev) return;
	try {
		mirror();
	} catch (error) {
		log('devStore failed', error);
	}
}

function publishStatus(): void {
	track(() => jarabotDevStore.trackStatus({ isReady, pendingCalls: pendingQueue.length }));
}

if (typeof window !== 'undefined') {
	if (window.$jarabot?.hasLoaded) {
		isReady = true;
	} else {
		window.addEventListener(
			'jarabot:ready',
			() => {
				isReady = true;
				pendingQueue.splice(0).forEach((fn) => {
					try {
						fn();
					} catch (error) {
						// one failing call must not swallow the rest of the queue
						log('queued call failed', error);
					}
				});
				publishStatus();
			},
			{ once: true }
		);
	}
	publishStatus();

	if (isDev) {
		(
			[
				'jarabot:ready',
				'jarabot:on-message',
				'jarabot:on-start-conversation',
				'jarabot:custom-action',
				'jarabot:error',
			] as const
		).forEach((eventName) => {
			window.addEventListener(eventName, (e: Event) => {
				log(`incoming:${eventName}`, (e as CustomEvent).detail);
			});
		});
	}
}

function schedule(fn: QueuedCall): void {
	if (isReady) {
		fn();
	} else {
		pendingQueue.push(fn);
	}
	publishStatus();
}

// function getIdentifierHash(country: Country): string | undefined {
// 	switch (country) {
// 		case 'CZ':
// 			return process.env.JARABOT_IDENTIFIER_HASH;
// 		case 'SK':
// 			return process.env.JARABOT_IDENTIFIER_HASH_SK;
// 		case 'HU':
// 			return process.env.JARABOT_IDENTIFIER_HASH_HU;
// 		default:
// 			return process.env.JARABOT_IDENTIFIER_HASH;
// 	}
// }

export const jarabotClient = {
	sendMessage(
		event: JarabotEvent,
		detailInfo?: Record<string, unknown>,
		hidden?: boolean,
		options?: { shake?: boolean }
	): void {
		log('sendMessage', { event, detailInfo, hidden, options });
		schedule(() => {
			window.$jarabot?.sendMessage({ message: event, detail_info: detailInfo, hidden });
			showPendingIndicator(options);
			track(() => jarabotDevStore.trackSendMessage(event, detailInfo, hidden));
		});
	},

	setUser(identifier: string, country: Country, attrs: JarabotUserIdentity): void {
		log('setUser', { identifier, country, attrs });
		schedule(() => {
			window.$jarabot?.setUser(identifier, {
				// identifier_hash: getIdentifierHash(country),
				...attrs,
			});
			track(() => jarabotDevStore.trackSetUser(identifier, attrs));
		});
	},

	setCustomAttributes(attrs: JarabotContactAttributes): void {
		log('setCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setCustomAttributes(attrs);
			track(() => jarabotDevStore.trackSetCustomAttributes(attrs));
		});
	},

	setConversationCustomAttributes(attrs: JarabotAttributes): void {
		log('setConversationCustomAttributes', attrs);
		schedule(() => {
			window.$jarabot?.setConversationCustomAttributes(attrs);
			track(() => jarabotDevStore.trackSetConversationCustomAttributes(attrs));
		});
	},

	deleteCustomAttribute(key: string): void {
		log('deleteCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteCustomAttribute(key);
			track(() => jarabotDevStore.trackDeleteCustomAttribute(key));
		});
	},

	deleteConversationCustomAttribute(key: string): void {
		log('deleteConversationCustomAttribute', key);
		schedule(() => {
			window.$jarabot?.deleteConversationCustomAttribute(key);
			track(() => jarabotDevStore.trackDeleteConversationCustomAttribute(key));
		});
	},

	reset(): void {
		log('reset');
		schedule(() => {
			window.$jarabot?.reset();
			track(() => jarabotDevStore.trackReset());
		});
	},

	toggle(state?: 'open' | 'close'): void {
		log('toggle', state);
		schedule(() => {
			window.$jarabot?.toggle(state);
			track(() => jarabotDevStore.trackToggle(state));
		});
	},
};
